import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Star, MapPin, Wrench, Phone, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const AdminTradiesPage = () => {
  const navigate = useNavigate();
  const { user, api, loading: authLoading } = useAuth();
  const [tradies, setTradies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || (!user.is_admin && user.email !== 'ebonnie_faith@hotmail.com' && user.email !== 'craftersmarketaus@gmail.com')) {
      navigate('/');
      return;
    }

    fetchTradies();
  }, [user, authLoading, navigate]);

  const fetchTradies = async () => {
    try {
      const res = await api().get('/admin/tradies');
      setTradies(res.data.tradies);
    } catch (err) {
      console.error('Failed to fetch tradies', err);
    } finally {
      setLoading(false);
    }
  };

  const getServiceName = (serviceId) => {
    const serviceMap = {
      'furniture-restoration': 'Furniture Restoration',
      'upholstery': 'Upholstery',
      'carpentry': 'Carpentry',
      'plumbing': 'Plumbing',
      'electrical': 'Electrical',
      'landscaping': 'Landscaping',
      'gardening': 'Gardening',
      'panel-beating': 'Panel Beating',
      'automotive-spray': 'Auto Spray',
      'marine-spray': 'Marine Spray',
      'tiling': 'Tiling',
      'plastering': 'Plastering',
      'roofing': 'Roofing',
      'fencing': 'Fencing',
      'concreting': 'Concreting',
      'flooring': 'Flooring',
      'cabinet-making': 'Cabinet Making',
      'glazing': 'Glazing',
      'air-conditioning': 'Air Con',
      'other': 'Other'
    };
    return serviceMap[serviceId] || serviceId;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-teal-600 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Tradespeople
            </h1>
            <p className="text-stone-500 mt-1">
              Sorted by highest to lowest ratings • {tradies.length} total
            </p>
          </div>
        </div>

        {tradies.length === 0 ? (
          <div className="bg-white border border-stone-200 rounded-lg p-12 text-center">
            <Wrench className="h-12 w-12 mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-900 mb-2">No tradies yet</h3>
            <p className="text-stone-500">Tradies will appear here once they enable trade services.</p>
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Tradie
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Rating
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Services
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-stone-500 uppercase tracking-wider">
                      Advertised
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {tradies.map((tradie, index) => (
                    <tr key={tradie.id} className="hover:bg-stone-50">
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                          index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-stone-200 text-stone-700' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-stone-100 text-stone-500'
                        }`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <Link to={`/profile/${tradie.id}`} className="flex items-center gap-3 hover:text-teal-600">
                          <div className="w-10 h-10 rounded-full bg-stone-100 overflow-hidden flex-shrink-0">
                            {tradie.profile_photo ? (
                              <img
                                src={tradie.profile_photo}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-lg font-bold text-stone-400">
                                {tradie.name?.charAt(0).toUpperCase() || '?'}
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-stone-900">{tradie.name}</p>
                            <p className="text-xs text-stone-500">{tradie.email}</p>
                          </div>
                        </Link>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          <Star className={`h-4 w-4 ${tradie.average_rating > 0 ? 'fill-yellow-400 text-yellow-400' : 'text-stone-300'}`} />
                          <span className="font-medium">{tradie.average_rating || '0.0'}</span>
                          <span className="text-stone-400 text-sm">({tradie.review_count})</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {tradie.trade_services?.slice(0, 2).map((s) => (
                            <span key={s} className="px-2 py-0.5 bg-teal-50 text-teal-600 text-xs rounded-full">
                              {getServiceName(s)}
                            </span>
                          ))}
                          {tradie.trade_services?.length > 2 && (
                            <span className="px-2 py-0.5 bg-stone-100 text-stone-500 text-xs rounded-full">
                              +{tradie.trade_services.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1 text-sm text-stone-600">
                          <MapPin className="h-3.5 w-3.5" />
                          {tradie.trade_service_area && `${tradie.trade_service_area}, `}
                          {tradie.trade_service_state || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {tradie.phone && (
                          <a href={`tel:${tradie.phone}`} className="flex items-center gap-1 text-sm text-stone-600 hover:text-teal-600 mb-1">
                            <Phone className="h-3.5 w-3.5" />
                            {tradie.phone}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {tradie.tradie_ad_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                            <CheckCircle className="h-3 w-3" />
                            Active
                          </span>
                        ) : (
                          <span className="text-stone-400 text-xs">No</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTradiesPage;
